import Configuration from './Configuration';
import MetaOptionList from './MetaOptionList';
import Noun from '../core/Noun';
import MetaOption from './MetaOption';
import GenericError from "../errors/GenericError"

class MetaField extends Noun {
  constructor(args = {}) {
    const {
      data = {},
      metaOverrideSet = null,
      // This should never have been needed, but we return shell meta fields and I don't want to touch that problem
      isEmpty = false,
    } = args;

    const {
      options,
      ...restData
    } = data;

    super({
      ...args,
      data: restData,
    });

    this._optionList = new MetaOptionList({ data: options, parent: this });
    this._isEmpty = isEmpty;

    // Let me look at the list of meta overrides that exist globally
    const globalOverrides = [...Configuration.listGlobalMetaOverrides()];
    // Let's also grab the set of overrides that a person has passed in specifically for this field
    const localOverrides = (metaOverrideSet) ? metaOverrideSet : [];
    // By default, global overrides exist earlier than local overrides
    const allOverrides = [...globalOverrides, ...localOverrides];

    const unprioritizedOverrides = allOverrides.filter(v => v.ordinal() === 0);
    const prioritizedOverrides = allOverrides.filter(v => v.ordinal() > 0).sort((a, b) => a.ordinal() - b.ordinal());
    const sortedOverrides = [
      ...unprioritizedOverrides,
      ...prioritizedOverrides,
    ];

    sortedOverrides.forEach(override => {
      override.apply(this);
    });
  }

  export() {
    const _attributeMap = super.export();

    return {
      ..._attributeMap,
      options: this._optionList.list().map(v => v.export()),
    };
  }

  id() {
    return this.attr('name');
  }

  isEmpty() {
    return !!this._isEmpty;
  }

  merge(other) {
    if (!this.shouldMerge(other)) {
      return;
    }

    super.merge(other);

    this._optionList.merge(other._optionList);

    return this;
  }

  options() {
    return this._optionList;
  }

  shallowCopy(metaField) {
    this.attributes(metaField.attributes())
    this._optionList = metaField._optionList; // Point this new meta field at a reference to the other meta fields options
  }

  applyExcludedUnless(attrName, resource) {
    // If a field is excluded, hide it, make it readonly, and make it not required
    // We set hidden so the field is not visible; not required so that we don't get validation errors
    // for it; readonly because it feels weird to have a field we can't see that is changeable
    if (resource.getExclusionList()[attrName]) {
      this.attr('required', false);
      this.attr('readonly', true);
      this.attr('hidden', true);
    }
    return this;
  }

  defaultValue() {
    if (this.attr('type') === 'multipicklist' && this.attr('default')) {
      return this.attr('default').split(",");
    }
    return this.attr('default');
  }

  option(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const option = this._optionList.item(key);

    if (!option) return new MetaOption({ parent: this._optionList });

    return option;
  }

  hasParentResource() {
    return this.getParentResource() !== null;
  }

  hasHint(hint) {
    if (this.attr('hint') === hint) {
      return true;
    }

    const hints = this.attr('hints') || [];
    return hints.includes(hint);
  }

  getParentResource() {
    let parent = this.parent();
    while (parent !== undefined) {
      // instanceOf Resource causes a circular import error
      if (parent.hasOwnProperty('resourceId')) {
        return parent;
      }
      parent = parent.parent();
    }
    return null;
  }

  resourceAttrValue() {
    if (!this.hasParentResource()) {
      throw new GenericError('no-parent-resource', 'Field must be a part of a resource to get a resource value.');
    }
    const resource = this.getParentResource();

    // If intent was to retrieve a value that could exist in both (e.g. id) then you would receive the wrong one.
    let value = resource.attr(this.id());
    if (value === undefined) {
      value = resource.attr('attributes')[this.id()];
    }
    return value;
  }

  // Deprecated: Use selectedOptions or selectedOptionPrompt in Resource
  selectedOption() {
    return this.options().item(this.resourceAttrValue());
  }

  // Deprecated: Use selectedOptions or selectedOptionPrompt in Resource
  selectedOptions(optionAttrName) {
    if (optionAttrName === undefined) {
      throw new Error('Usage: optionAttrName is required.');
    }

    let attrValues = this.resourceAttrValue();

    if (!Array.isArray(attrValues)) {
      attrValues = [attrValues];
    }

    return attrValues.map(v => this.option(v))
      .map(option => option.attr(optionAttrName));
  }
}

export default MetaField;
